'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/lib/stores/auth-store'
import { useDynamicFontLoader } from '@/components/dynamic-font-loader'

export interface ColorScheme {
  name: string
  primary: string
  secondary: string
  accent: string
  background?: string
  gradient?: string
}

export interface ThemePreferences {
  fontFamily: string
  fontSize: number
  colorScheme: ColorScheme
  lightThemeColor?: string
  darkThemeColor?: string
  theme?: 'light' | 'dark' | 'system'
}

const defaultColorScheme: ColorScheme = {
  name: 'OrbitFlow Teal',
  primary: 'hsl(163, 60%, 38%)',
  secondary: 'hsl(183, 75%, 44%)',
  accent: 'hsl(163, 60%, 38%)'
}

const defaultPreferences: ThemePreferences = {
  fontFamily: 'Varela Round',
  fontSize: 16,
  colorScheme: defaultColorScheme
}

export function useThemePreferences() {
  const [preferences, setPreferences] = useState<ThemePreferences>(defaultPreferences)
  const [isLoaded, setIsLoaded] = useState(false)
  const [hasLoadedFromFirebase, setHasLoadedFromFirebase] = useState(false)
  const { user } = useAuth()

  // Dynamically load the selected font on demand
  useDynamicFontLoader(preferences.fontFamily)

  // Listen for font changes from language switches
  useEffect(() => {
    const handleStorageChange = () => {
      const fontFamily = localStorage.getItem('fontFamily')
      if (fontFamily && fontFamily !== preferences.fontFamily) {
        setPreferences(prev => {
          // Only update if actually different
          if (prev.fontFamily !== fontFamily) {
            return { ...prev, fontFamily }
          }
          return prev
        })
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--font-family', fontFamily)
        }
      }
    }

    const handleFontChanged = (event: Event) => {
      const customEvent = event as CustomEvent
      const fontFamily = customEvent.detail?.fontFamily
      if (fontFamily && fontFamily !== preferences.fontFamily) {
        setPreferences(prev => {
          // Only update if actually different
          if (prev.fontFamily !== fontFamily) {
            return { ...prev, fontFamily }
          }
          return prev
        })
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--font-family', fontFamily)
        }
      }
    }

    // Listen for storage events from other tabs/windows
    window.addEventListener('storage', handleStorageChange)
    
    // Listen for custom fontChanged events
    window.addEventListener('fontChanged', handleFontChanged)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('fontChanged', handleFontChanged)
    }
  }, []) // Remove preferences.fontFamily dependency to prevent loops

  // Load preferences from Firebase first, then localStorage as fallback
  useEffect(() => {
    // Prevent re-loading if already loaded
    if (isLoaded && hasLoadedFromFirebase) return

    const loadPreferences = async () => {
      // First, always check localStorage (which may have been synced by useSyncPreferences)
      const stored = localStorage.getItem('orbitflow-theme-preferences')
      if (stored) {
        try {
          const parsed = JSON.parse(stored)
          setPreferences({ ...defaultPreferences, ...parsed })
          // Remove verbose localStorage log
        } catch (e) {
          console.error('Failed to parse theme preferences:', e)
        }
      }

      try {
        // Then try to load from Firebase if user is authenticated
        if (user?.id && !hasLoadedFromFirebase) {
          const response = await fetch(`/api/user-preferences?userId=${user.id}`)
          if (response.ok) {
            const data = await response.json()
            if (data.prefs) {
              // Build preferences from the same structure as EOD reminders
              const loadedPrefs: Partial<ThemePreferences> = {}
              
              if (data.prefs.fontFamily) loadedPrefs.fontFamily = data.prefs.fontFamily
              if (data.prefs.fontSize) loadedPrefs.fontSize = data.prefs.fontSize
              if (data.prefs.colorScheme) loadedPrefs.colorScheme = data.prefs.colorScheme
              if (data.prefs.theme) loadedPrefs.theme = data.prefs.theme
              
              if (Object.keys(loadedPrefs).length > 0) {
                // Merge with current state (from localStorage) instead of defaults
                setPreferences(prev => ({ ...prev, ...loadedPrefs }))
                // Remove verbose Firebase log
              }
            }
          }
          setHasLoadedFromFirebase(true)
        }
      } catch (error) {
        console.error('Failed to load theme preferences from Firebase:', error)
      }

      setIsLoaded(true)
    }

    loadPreferences()
  }, [user?.id, isLoaded, hasLoadedFromFirebase])

  // Apply preferences to document
  useEffect(() => {
    if (!isLoaded) return

    const root = document.documentElement

    // Mark that we have a custom theme active
    root.setAttribute('data-custom-theme', 'true')

    // Apply font family
    root.style.setProperty('--font-family', preferences.fontFamily)

    // Apply font size
    root.style.setProperty('--font-size-base', `${preferences.fontSize}px`)

    // Apply color scheme
    if (preferences.colorScheme.primary) {
      // Handle both hsl() format and raw HSL values
      let primaryHSL = preferences.colorScheme.primary

      if (primaryHSL.startsWith('hsl(')) {
        primaryHSL = primaryHSL.replace('hsl(', '').replace(')', '')
      }
      // CRITICAL: Remove commas to convert from "234, 78%, 24%" to "234 78% 24%"
      // CSS custom properties need space-separated values to work with hsl(var(--primary))
      primaryHSL = primaryHSL.replace(/,/g, '').trim()

      root.style.setProperty('--primary', primaryHSL)

      // Sync --of-violet design tokens so all consumers respond to color changes
      root.style.setProperty('--of-violet', `hsl(${primaryHSL})`)
      const match = primaryHSL.match(/(\d+)\s+(\d+)%?\s+(\d+)%?/)
      if (match) {
        const [, h, s, l] = match
        const hN = parseInt(h), sN = parseInt(s), lN = parseInt(l)
        root.style.setProperty('--primary-dark', `${hN} ${sN}% ${Math.min(lN + 10, 65)}%`)
        root.style.setProperty('--of-violet-deep', `hsl(${hN} ${sN}% ${Math.max(lN - 12, 20)}%)`)
        root.style.setProperty('--of-violet-soft', `hsl(${hN} ${Math.round(sN * 0.45)}% ${Math.min(lN + 28, 95)}%)`)
        root.style.setProperty('--of-violet-ink', `hsl(${hN} ${Math.round(sN * 0.65)}% 18%)`)
      }
    }
    if (preferences.colorScheme.secondary) {
      let secondaryHSL = preferences.colorScheme.secondary
      if (secondaryHSL.startsWith('hsl(')) {
        secondaryHSL = secondaryHSL.replace('hsl(', '').replace(')', '')
      }
      // Remove commas and normalize to space-separated format
      secondaryHSL = secondaryHSL.replace(/,/g, '').trim()
      root.style.setProperty('--secondary', secondaryHSL)
    }
    if (preferences.colorScheme.accent) {
      let accentHSL = preferences.colorScheme.accent
      if (accentHSL.startsWith('hsl(')) {
        accentHSL = accentHSL.replace('hsl(', '').replace(')', '')
      }
      // Remove commas and normalize to space-separated format
      accentHSL = accentHSL.replace(/,/g, '').trim()
      root.style.setProperty('--accent', accentHSL)
      root.style.setProperty('--accent-custom', accentHSL)
          }

    // Set button background (solid color only, no gradients)
    root.style.setProperty('--button-hover-bg', preferences.colorScheme.primary)
    root.classList.remove('gradient-background')

    // Update favicon color to match theme
    updateFavicon(preferences.colorScheme.primary)
  }, [preferences, isLoaded])

  // Function to update favicon with theme color
  const updateFavicon = (color: string) => {
    if (typeof document === 'undefined') return
    try {
      // Convert HSL to hex for SVG
      const hslMatch = color.match(/hsl\((\d+),?\s*(\d+)%?,?\s*(\d+)%?\)/)
      let rgb = { r: 94, g: 61, b: 155 } // Default purple
      if (hslMatch) {
        const [, h, s, l] = hslMatch.map(Number)
        rgb = hslToRgb(h, s / 100, l / 100)
      }
      const hexColor = `#${rgb.r.toString(16).padStart(2, '0')}${rgb.g.toString(16).padStart(2, '0')}${rgb.b.toString(16).padStart(2, '0')}`
      
      // Create SVG favicon using the OrbitFlow orbit mark with theme color
      const svg = `<svg width="48" height="48" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <mask id="cut">
      <rect width="128" height="128" fill="white"/>
      <circle cx="29" cy="34" r="24" fill="black"/>
    </mask>
  </defs>
  <circle cx="64" cy="64" r="44" stroke="${hexColor}" stroke-width="16" fill="none" mask="url(#cut)"/>
  <circle cx="29" cy="34" r="14" stroke="${hexColor}" stroke-width="16" fill="none"/>
</svg>`.trim()
      
      // Convert SVG to data URL
      const svgBlob = new Blob([svg], { type: 'image/svg+xml' })
      const url = URL.createObjectURL(svgBlob)
      
      // Always replace favicon link
      let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement
      if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
      }
      link.type = 'image/svg+xml'
      link.href = url
      // Remove any existing favicon links
      const head = document.getElementsByTagName('head')[0]
      const links = head.querySelectorAll("link[rel*='icon']")
      links.forEach(l => {
        if (l !== link) head.removeChild(l)
      })
      if (!link.parentNode) {
        head.appendChild(link)
      }
    } catch (error) {
      console.error('Failed to update favicon:', error)
    }
  }

  // Helper function to convert HSL to RGB
  const hslToRgb = (h: number, s: number, l: number) => {
    let r, g, b

    if (s === 0) {
      r = g = b = l
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1
        if (t > 1) t -= 1
        if (t < 1/6) return p + (q - p) * 6 * t
        if (t < 1/2) return q
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6
        return p
      }

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s
      const p = 2 * l - q
      r = hue2rgb(p, q, h / 360 + 1/3)
      g = hue2rgb(p, q, h / 360)
      b = hue2rgb(p, q, h / 360 - 1/3)
    }

    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255)
    }
  }

  const updatePreferences = (updates: Partial<ThemePreferences>) => {
    const newPreferences = { ...preferences, ...updates }
    setPreferences(newPreferences)
    
    // Save to localStorage for immediate access
    localStorage.setItem('orbitflow-theme-preferences', JSON.stringify(newPreferences))
    
    // Save to Firebase if user is authenticated - use same structure as EOD reminders
    if (user?.id) {
      fetch('/api/user-preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          preferences: {
            fontFamily: newPreferences.fontFamily,
            fontSize: newPreferences.fontSize,
            colorScheme: newPreferences.colorScheme,
            theme: newPreferences.theme
          }
        })
      }).catch(error => {
        console.error('Failed to save theme preferences to Firebase:', error)
      })
    }
  }

  const updateColorScheme = (scheme: ColorScheme) => {
    updatePreferences({ colorScheme: scheme })
  }

  const updateFontFamily = (fontFamily: string) => {
    updatePreferences({ fontFamily })

    // Also update the separate fontFamily key in localStorage for language hook compatibility
    localStorage.setItem('fontFamily', fontFamily)

    // Force immediate CSS update
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--font-family', fontFamily)
    }
  }

  const updateFontSize = (fontSize: number) => {
    updatePreferences({ fontSize })
  }

  const resetToDefaults = () => {
    setPreferences(defaultPreferences)
    localStorage.removeItem('orbitflow-theme-preferences')
  }

  return {
    preferences,
    updatePreferences,
    updateColorScheme,
    updateFontFamily,
    updateFontSize,
    resetToDefaults,
    isLoaded
  }
}
